---
title: Troubleshooting
lede: Common failures, what they mean, and what to do.
order: 54
part: Help
---

### Airlock is already running

Only one Airlock can use a data folder. Quit the other one, or give this one its own [`AIRLOCK_HOME`](/data-folder). After a crash, the next launch takes the folder over by itself.

### A licence wall covers the window

- *Your trial has ended*: press **Buy license**.
- *Your license has ended*: press **Renew license**, then **Check again**. Your key does not change.
- *This computer is not on your license*: nothing expired, but every machine slot is taken. **Manage machines** opens your account, where you remove one. A machine unseen for a month frees its slot by itself. If an organization cut this computer off, the wall says which one: ask its administrators to allow it again, then press **Check again**.
- *…’s seats have stopped*: the organization no longer pays for its seats. Its owner or an administrator buys seats on Billing in the account area, and the window unlocks.

Running sessions keep working behind the wall. See [Your licence key](/licence).

### The preview says the page did not load

![The preview error](media/browser-error.webp)

The sentence under the heading says what happened:

- *No process is listening on port N inside the VM.* The server is still starting, has exited, or listens on another port. Its log in the dock says which. Listening on `127.0.0.1` is fine; Airlock relays it.
- *The session's VM is not taking connections on port N.* It may be parked, stopped or still booting. Open the session to wake it.
- *Nothing answered on port N on this computer.* On a session on this computer, the process is still starting or has stopped.
- *Port N accepted the connection and closed it without answering.* On a session on this computer, it is still starting, or it does not serve HTTP there.
- *The preview lost its connection to the sandbox.* The machine is unreachable, not just the process.

Press **Retry** to reload.

### A session cannot reach a host it needs

A micro-VM refuses a host nobody listed. With **Ask** or **Allow**, Airlock puts the host to you, and allowing it moves the session to a new machine that reaches it on ports 80 and 443, usually a few seconds later. With **Block — allowlist only**, nothing asks: add the host in [Network](/network) and start a new session.

### The first session after an image change is slow

The session image is being rebuilt. The next session starts from the new snapshot. See [The session image](/session-image).

### A task I started does not start

It is waiting for a free slot, at the top of **To do**, and the *In development* lane shows `3/3 BUSY`. Answer, merge or delete a session that is waiting on you, or delete a finished one to free a machine. See [Defaults and limits](/limits).

### A session says it is waiting for input, but the agent is busy

Some prompts send no signal, so Airlock assumes the agent is waiting after a delay. Raise **Idle fallback** in **Settings › All agents**.

### A diff or a sync fails with a disk error

Git may say « Out of diskspace » or « No space left on device ». Airlock says under the error that the disk is full. In a micro-VM that is the session's own disk: free space in it, such as a build folder or a cache, or raise **Settings › Sandbox › Disk** for sessions started afterwards. See [Defaults and limits](/limits).

### The data folder is very large

**Diagnostics › Data folder** (`⌘⌥D`) shows which area is large. **Sweep orphans** removes what deleted sessions left behind, and deleting sessions you have finished with frees the rest.

### A sync gave up on its own

The source branch moved and rebasing onto it would conflict, so the automatic sync was abandoned. Press **Sync** on the review footer. When it stops with *Sync stopped*, resolve the files it names and press **Sync again**, or **Abandon** to undo it. See [Sync and conflicts](/sync).

### A merge says the branch and its remote have both moved on

Your target branch and its remote each have commits the other lacks. Bring the branch up to date with its remote in your own checkout, then merge again.

### A push is refused

- **Workflow scope.** GitHub refuses a push that changes `.github/workflows` unless the sign-in has the `workflow` scope. In **Settings › GitHub**, press **Sign out**, then **Connect** again, and push again.
- **Permission.** The signed-in account cannot write to the repository. **Settings › GitHub** shows which account it is; connect one that can.
- **The branch moved.** Sync the session, then push again. Airlock never force-pushes.

### A merge did not appear on GitHub

Merging is local unless **Push after merging** was on. After a merge, the **Pushed** step in [Changes](/changes) has **Push** for each repository, or **Push all**. See [Merging](/merging).

### Something else looks wrong

Open Diagnostics with `⌘⌥D`. Send what the Overview reports, with **Settings › About**, to [support@tryairlock.app](mailto:support@tryairlock.app).
